// Clones the production portrait game screen as closely as possible against a
// fixture: the top bar (cog menu + player roster strip), the map with corner
// control chips (zoom + region-details), the expandable roster panel, and the
// "player out" notice modal. Dev-harness only — renders the real components
// with fixture props; touches no app data/network layer.
import React from 'react'
import { Pressable, Text, TouchableOpacity, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ActiveGameView } from '../components/game/screen/ActiveGameView'
import {
  PlayerRosterStrip,
  PlayerRosterPanel,
} from '../components/game/PlayerRosterPanel'
import { PlayerOutModal } from '../components/game/PlayerOutModal'
import { GameMenu } from '../components/game/GameMenu'
import { useOrientation } from '../hooks/useOrientation'
import { buildActiveGameViewProps } from './buildActiveGameViewProps'
import type { PreviewScenario } from './scenarios'
import type { PlacementAction } from '../lib/placementStack'
import type { PlacementStepMode } from '../components/map/PlacementHud'

type ActiveCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

// Map corner control chip — identical styling to GameScreen's zoom/region chips.
function ControlChip({
  emoji,
  active,
  onPress,
  label,
}: {
  emoji: string
  active: boolean
  onPress: () => void
  label: string
}) {
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      className={`border-2 rounded-full items-center justify-center ${
        active ? 'bg-amber-200 border-amber-700' : 'bg-paper border-olive'
      }`}
      style={{ width: 40, height: 40 }}
    >
      <Text style={{ fontSize: 18, lineHeight: 22 }}>{emoji}</Text>
    </TouchableOpacity>
  )
}

export function PreviewGameScreen({ scenario }: { scenario: PreviewScenario }) {
  const insets = useSafeAreaInsets()
  const { isLandscape } = useOrientation()
  const [rosterExpanded, setRosterExpanded] = React.useState(false)
  const [zoomEnabled, setZoomEnabled] = React.useState(false)
  const [zoomScale, setZoomScale] = React.useState(1)
  const [panOffset, setPanOffset] = React.useState({ tx: 0, ty: 0 })
  const [regionDetailsMode, setRegionDetailsMode] = React.useState(false)
  // The preview uses the real placement components. Keep their lifted state
  // live so a territory tap can be observed by Maestro just like it is in the
  // production game screen; the static props builder only supplies defaults.
  const [placementStack, setPlacementStack] = React.useState<PlacementAction[]>([])
  const [placementStepMode, setPlacementStepMode] = React.useState<PlacementStepMode>(1)

  const base = React.useMemo(
    () => buildActiveGameViewProps(scenario.game, { viewerSlot: scenario.viewerSlot }),
    [scenario],
  )

  const viewerSlot = scenario.viewerSlot ?? 1
  const presence = React.useMemo(
    () => new Set(base.game.players.map((p) => p.userId)),
    [base.game],
  )

  const regionCorner =
    (base.gameState.map as { regionToggleCorner?: ActiveCorner }).regionToggleCorner ??
    'bottom-right'
  const regionToggleNode = (
    <View style={{ gap: 8 }}>
      <ControlChip
        emoji="🔍"
        active={zoomEnabled}
        label="Toggle map zoom"
        onPress={() => {
          setZoomEnabled((on) => !on)
          setZoomScale((s) => (s === 1 ? 2 : 1))
          setPanOffset({ tx: 0, ty: 0 })
        }}
      />
      <ControlChip
        emoji="🗺"
        active={regionDetailsMode}
        label="Toggle region bonuses view"
        onPress={() => setRegionDetailsMode((v) => !v)}
      />
    </View>
  )

  // Cog + roster row — reused for the portrait top bar and, in landscape, the
  // header pinned at the top of the action column (mirrors the real screen).
  const headerRow = (
    <>
      <GameMenu
        gameId={base.game.id}
        canResign
        trigger={(open) => (
          <TouchableOpacity
            onPress={open}
            accessibilityLabel="Open game menu"
            accessibilityRole="button"
            className="bg-paper border-2 border-olive rounded-full items-center justify-center"
            style={{ width: 38, height: 38, flexShrink: 0 }}
          >
            <Text className="text-ink text-base">⚙</Text>
          </TouchableOpacity>
        )}
      />
      <PlayerRosterStrip
        state={base.gameState}
        players={base.game.players}
        currentPlayerIndex={base.game.currentPlayerIndex}
        onExpand={() => setRosterExpanded(true)}
        expanded={rosterExpanded}
        orientation="portrait"
        presence={presence}
      />
    </>
  )

  const landscapeActionHeader = isLandscape ? (
    <View
      className="flex-row items-center gap-2 pt-2 pb-1"
      style={{ paddingLeft: 4, paddingRight: 2 }}
    >
      {headerRow}
    </View>
  ) : null

  const props = {
    ...base,
    isLandscape,
    activePhaseView: regionDetailsMode ? ('region-details' as const) : base.activePhaseView,
    regionDetailsMode,
    regionToggleNode,
    regionToggleCorner: regionCorner,
    zoomToggleNode: null,
    landscapeActionHeader,
    zoomEnabled,
    zoomScale,
    panOffset,
    onPanChange: setPanOffset,
    onZoomScaleChange: setZoomScale,
    placementStack,
    onPlacementStackChange: setPlacementStack,
    placementStepMode,
    onPlacementStepModeChange: setPlacementStepMode,
  }

  const eliminated = base.gameState.eliminated ?? []
  const outSlot = eliminated.length ? Number(eliminated[eliminated.length - 1]) : null
  const [showNotice, setShowNotice] = React.useState(outSlot != null)
  const outPlayer = outSlot != null ? base.game.players.find((p) => p.slot === outSlot) : undefined
  const byPlayer = base.game.players.find((p) => p.slot === viewerSlot)

  return (
    <View className="flex-1 bg-canvas">
      {/* Portrait top bar — cloned from GameScreen: [cog | roster]. In landscape
          the same row moves INTO the action column via landscapeActionHeader. */}
      {!isLandscape && (
        <View
          className="flex-row items-center gap-2 px-2 py-1.5"
          style={{ paddingTop: insets.top + 6 }}
          accessibilityLabel="Game top bar"
        >
          {headerRow}
        </View>
      )}

      <View style={{ flex: 1, paddingTop: isLandscape ? insets.top : 0 }}>
        <ActiveGameView {...props} />
      </View>

      <PlayerRosterPanel
        state={props.gameState}
        players={props.game.players}
        currentPlayerIndex={props.game.currentPlayerIndex}
        viewerUserId={props.viewerUserId}
        expanded={rosterExpanded}
        onCollapse={() => setRosterExpanded(false)}
        presence={presence}
      />

      {showNotice && outSlot != null && (
        <PlayerOutModal
          key={outSlot}
          reason="eliminated"
          playerName={outPlayer?.displayName ?? `Player ${outSlot}`}
          playerSlot={outSlot}
          bySlotName={byPlayer?.displayName ?? null}
          bySlot={byPlayer?.slot ?? null}
          onClose={() => setShowNotice(false)}
        />
      )}

      {!showNotice && outSlot != null && (
        <Pressable
          onPress={() => setShowNotice(true)}
          style={{
            position: 'absolute',
            bottom: insets.bottom + 8,
            alignSelf: 'center',
            // Above the game's map/panel stack, which GameMapHost raises (zIndex 5)
            // and which reaches the screen bottom under these pills.
            zIndex: 1000,
            elevation: 1000,
            backgroundColor: 'rgba(0,0,0,0.55)',
            borderRadius: 16,
            paddingVertical: 5,
            paddingHorizontal: 12,
          }}
        >
          <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>
            ⟲ Replay elimination notice
          </Text>
        </Pressable>
      )}
    </View>
  )
}
